import React, { useState } from 'react'

import PropTypes from 'prop-types'
import { Controller, FormContext, useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@material-ui/core'

import { FileUploadInput, Label, LoadingFeedback } from 'components'

import useSnackbar from 'hooks/useSnackbar'
import useIncidentManegement from 'hooks/useIncidentManegement'

import constants from 'constants/index'
import * as service from 'service'
import schema from './schema'

const RiskAssumedDialog = ({
  open,
  setOpen,
  incident = {},
  refresh,
  handleNext,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const snackbar = useSnackbar()
  const { resetAdvanceStep } = useIncidentManegement()

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      justification:
        incident?.riskAssumptions?.communication?.justification || '',
      attachments:
        incident?.riskAssumptions?.communication?.attachments || null,
    },
  })

  const { control, errors, handleSubmit, reset } = formMethods

  const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      if (incident?.riskAssumptions?.communication?.id) {
        await service.dponet.incidentRiskAssumptions.update({
          incidentId: incident.id,
          id: incident.riskAssumptions.communication.id,
          incidentRiskAssumption: {
            justification: data.justification,
            attachments: data.attachments,
          },
        })
      } else {
        await service.dponet.incidentRiskAssumptions.create({
          incidentId: incident.id,
          incidentRiskAssumption: {
            justification: data.justification,
            attachments: data.attachments,
            step: constants.incidentRiskAssumption.COMMUNICATION_STEP,
          },
        })
      }

      snackbar.open({
        message: 'Risco assumido com sucesso!',
        variant: 'success',
      })

      await service.dponet.incidents.concludeStep({
        incidentId: incident.id,
        step: constants.incidentManegement.COMMUNICATION_ENUM,
      })

      refresh()
      resetAdvanceStep()
      handleNext()
      handleClose()
    } catch (error) {
      snackbar.open({
        message: 'Erro ao assumir o risco. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset({})
    setOpen(false)
  }

  const handleRemoveApiFile = async (fileId) => {
    try {
      await service.dponet.incidentRiskAssumptions.destroyAttachment({
        incidentId: incident.id,
        id: incident.riskAssumptions.communication.id,
        fileId: fileId,
      })
    } catch (error) {
      snackbar.open({
        message: 'Erro ao remover arquivo. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <>
      <LoadingFeedback open={isLoading} />
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h4">
              Justificativa para Assumir o Risco
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />

        <FormContext {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} id="risk-justification-form">
            <DialogContent>
              <Box display="flex" flexDirection="column" gridGap={16}>
                <Label
                  title="Justificativa *"
                  description="Descreva detalhadamente a justificativa para assumir este risco"
                  paddingBoxTitle={0}
                >
                  <Controller
                    as={
                      <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        minRows={4}
                        error={!!errors.justification}
                        helperText={errors?.justification?.message}
                        placeholder="Digite aqui a justificativa para assumir o risco..."
                      />
                    }
                    control={control}
                    name="justification"
                  />
                </Label>

                <Label
                  title="Anexo *"
                  description="Anexe documentos que fundamentem a decisão de assumir o risco"
                  paddingBoxTitle={0}
                >
                  <FileUploadInput
                    controlName="attachments"
                    maxSize={constants.validations.MAX_FILE_SIZE_5_MB}
                    multiple
                    controllerBar
                    hasDownloadFile
                    onRemoveApiFile={handleRemoveApiFile}
                  />
                </Label>
              </Box>
            </DialogContent>
          </form>
        </FormContext>

        <Divider />
        <DialogActions>
          <Box
            display="flex"
            width="100%"
            gridGap={8}
            p={2}
            justifyContent="flex-end"
          >
            <Button type="button" variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              form="risk-justification-form"
            >
              Continuar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

RiskAssumedDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  incident: PropTypes.object,
  handleNext: PropTypes.func.isRequired,
}

export default RiskAssumedDialog
